import React from 'react';
import { useI18n } from '../../../i18n';
import { DocxViewer } from './DocxViewer';
import { XlsxViewer } from './XlsxViewer';
import { PdfViewer } from './PdfViewer';

export interface SourceLocation {
    quote: string;
    startIndex: number;
    endIndex: number;
    location: {
        paragraphIndex?: number;
        sheet?: string;
        row?: number;
        column?: string;
        page?: number;
    };
    confidence?: 'high' | 'medium' | 'low';
}

interface DocumentViewerProps {
    fileUrl: string;
    fileType: 'docx' | 'xlsx' | 'pdf' | 'txt';
    metadata?: {
        pageCount?: number;
        sheetNames?: string[];
        paragraphCount?: number;
    };
    highlightedSources?: Array<Pick<SourceLocation, 'quote'>>;
    activeRuleId?: string;
    scrollToSource?: Pick<SourceLocation, 'quote'> | null;
    onScrollComplete?: () => void;
    exactMatch?: boolean;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
    fileUrl,
    fileType,
    metadata,
    highlightedSources = [],
    activeRuleId,
    scrollToSource,
    onScrollComplete,
    exactMatch,
}) => {
    const { t } = useI18n();
    if (!fileUrl) {
        return (
            <div className="flex items-center justify-center h-full bg-gray-50">
                <div className="text-gray-500">{t.playbooks.viewer.noSourceDocument}</div>
            </div>
        );
    }

    switch (fileType) {
        case 'docx':
        case 'txt': {
            // Use quote text for highlighting instead of indices (indices don't match rendered HTML)
            const highlightQuotes = highlightedSources.map((source, idx) => ({
                quote: source.quote,
                id: `source-${idx}`,
            }));

            return (
                <DocxViewer
                    fileUrl={fileUrl}
                    highlightQuotes={highlightQuotes}
                    scrollToQuote={scrollToSource?.quote}
                    onScrollComplete={onScrollComplete}
                    exactMatch={exactMatch}
                />
            );
        }

        case 'xlsx': {
            // Use quote text for highlighting (more reliable than cell coordinates)
            const highlightQuotes = highlightedSources.map((source, idx) => ({
                quote: source.quote,
                id: `source-${idx}`,
            }));

            return (
                <XlsxViewer
                    fileUrl={fileUrl}
                    highlightQuotes={highlightQuotes}
                    scrollToQuote={scrollToSource?.quote}
                    onScrollComplete={onScrollComplete}
                />
            );
        }

        case 'pdf': {
            // Use quote text for highlighting in the PDF text layer
            const highlightQuotes = highlightedSources.map((source, idx) => ({
                quote: source.quote,
                id: `source-${idx}`,
            }));

            return (
                <PdfViewer
                    fileUrl={fileUrl}
                    highlightQuotes={highlightQuotes}
                    scrollToQuote={scrollToSource?.quote}
                    onScrollComplete={onScrollComplete}
                />
            );
        }

        default:
            return (
                <div className="flex items-center justify-center h-full bg-gray-50">
                    <div className="text-gray-500">{t.playbooks.viewer.unsupportedFileType(fileType)}</div>
                </div>
            );
    }
};
